import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Phone, Plus } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import AddNoteModal from '../ProjectTracking/AddNoteModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#39;/g, "'")
    .trim();
}

const AddCallInline = ({ zohoProjectId, onClose, onSaved }) => {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!content.trim()) { toast.error('Enter what was discussed'); return; }
    setSaving(true);
    try {
      await api.post(`${API}/zoho/project/${zohoProjectId}/calls`, { content: content.trim() });
      toast.success('Call logged in CRM');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="spellers-add-call-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900 mb-2">Log a Call</h3>
        <textarea
          autoFocus
          rows={4}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What was discussed…"
          className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          data-testid="spellers-add-call-content"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-500" data-testid="spellers-add-call-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-1.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
            style={{ backgroundColor: '#0f766e' }}
            data-testid="spellers-add-call-submit"
          >
            {saving ? 'Saving…' : 'Log Call'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Last 2 points of contact (Notes + logged Calls merged, most recent first) —
 * shown regardless of which one was used to log it. */
export const SpellersCommsRow = ({ project }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addNote, setAddNote] = useState(false);
  const [addCall, setAddCall] = useState(false);
  const [tick, setTick] = useState(0);
  const zohoId = project.zoho_project_id;

  const load = useCallback(() => {
    if (!zohoId) { setLoading(false); return; }
    setLoading(true);
    Promise.allSettled([
      api.get(`${API}/zoho/project/${zohoId}/notes`, { params: { limit: 5 } }),
      api.get(`${API}/zoho/project/${zohoId}/calls`, { params: { limit: 5 } }),
    ]).then(([notesRes, callsRes]) => {
      const notes = (notesRes.value?.data?.notes || []).map((n) => ({ ...n, kind: 'note' }));
      const calls = (callsRes.value?.data?.calls || []).map((c) => ({ ...c, kind: 'call' }));
      const merged = [...notes, ...calls]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 2);
      setItems(merged);
    }).finally(() => setLoading(false));
  }, [zohoId, tick]);

  useEffect(() => { load(); }, [load]);

  if (!zohoId) return null;

  return (
    <div className="px-4 py-2.5 bg-white border-l-4 border-blue-300 border-t border-slate-200 min-w-0 max-w-full" data-testid={`spellers-comms-row-${project.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Last Communication</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setAddNote(true)} className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-300 rounded-full px-2.5 py-1 hover:bg-blue-100 transition-colors whitespace-nowrap" data-testid={`spellers-add-note-btn-${project.id}`}>
            <Plus className="w-3 h-3" /> Note
          </button>
          <button onClick={() => setAddCall(true)} className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-300 rounded-full px-2.5 py-1 hover:bg-teal-100 transition-colors whitespace-nowrap" data-testid={`spellers-add-call-btn-${project.id}`}>
            <Plus className="w-3 h-3" /> Call
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-[11px] text-gray-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[11px] text-gray-400">No notes or calls logged yet</div>
      ) : (
        <div className="space-y-1 min-w-0">
          {items.map((it) => (
            <div key={it.id} className="flex items-start gap-1.5 text-[11px] min-w-0" data-testid={`spellers-comm-item-${it.id}`}>
              {it.kind === 'call' ? <Phone className="w-3 h-3 text-teal-500 mt-0.5 flex-shrink-0" /> : <MessageSquare className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />}
              <span className="text-gray-600 whitespace-normal min-w-0 flex-1" style={{ overflowWrap: 'anywhere' }}>{stripHtml(it.content) || it.title || it.subject}</span>
              {it.owner_name && <span className="text-gray-300 flex-shrink-0">{it.owner_name}</span>}
            </div>
          ))}
        </div>
      )}
      {addNote && (
        <AddNoteModal
          target={{ type: 'project', id: zohoId, name: project.project_name }}
          onClose={() => setAddNote(false)}
          onSaved={() => { setAddNote(false); setTick((t) => t + 1); }}
        />
      )}
      {addCall && (
        <AddCallInline zohoProjectId={zohoId} onClose={() => setAddCall(false)} onSaved={() => { setAddCall(false); setTick((t) => t + 1); }} />
      )}
    </div>
  );
};

export default SpellersCommsRow;
